import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { 
  Camera, 
  Plus, 
  Edit3, 
  Trash2, 
  Calendar, 
  Building2, 
  Users,
  MoreVertical,
  Video,
  Image as ImageIcon,
  Play,
  FileImage,
  FileVideo,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

// Utility functions for media type detection
const getFileExtension = (fileName: string) => {
  return fileName.split('.').pop()?.toLowerCase() || '';
};

const isVideoFile = (fileName: string) => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'm4v'];
  return videoExtensions.includes(getFileExtension(fileName));
};

const isImageFile = (fileName: string) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  return imageExtensions.includes(getFileExtension(fileName));
};

const getMediaTypeInfo = (fileName: string) => {
  if (isVideoFile(fileName)) {
    return {
      type: 'video',
      icon: Video,
      badge: 'Video',
      color: 'bg-blue-100 text-blue-700 border-blue-200'
    };
  } else if (isImageFile(fileName)) {
    return {
      type: 'image',
      icon: ImageIcon,
      badge: 'Image',
      color: 'bg-green-100 text-green-700 border-green-200'
    };
  } else {
    return {
      type: 'unknown',
      icon: FileImage,
      badge: 'Media',
      color: 'bg-gray-100 text-gray-700 border-gray-200'
    };
  }
};

export default function TeamImages() {
  const [, setLocation] = useLocation();
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [imageToDelete, setImageToDelete] = useState<any>(null);
  const { toast } = useToast();

  // Keyboard support for closing modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedVideo) {
        setSelectedVideo(null);
      }
    };

    if (selectedVideo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedVideo]);

  // Fetch team images
  const { data: teamImagesData, isLoading } = useQuery({
    queryKey: ['/team-images'],
  }) as { data: { teamImages: any[] } | undefined; isLoading: boolean };

  // Fetch departments for filtering
  const { data: departmentsData } = useQuery({
    queryKey: ['/admin/departments'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/departments");
      return response.json();
    },
  }) as { data: any[] | undefined };

  // Delete team image mutation
  const deleteTeamImageMutation = useMutation({
    mutationFn: (imageId: string) => apiRequest('DELETE', `/team-images/${imageId}`),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Team image deleted successfully!",
        variant: "default",
      });
      queryClient.invalidateQueries({ queryKey: ['/team-images'] });
      setImageToDelete(null); // Close the dialog after successful deletion
    },
    onError: (error: Error) => {
      console.error("Error deleting team image:", error);
      toast({
        title: "Error",
        description: "Failed to delete team image. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Filter team images by department
  const filteredTeamImages = teamImagesData?.teamImages?.filter(image => 
    selectedDepartment === "all" || (image.departmentId && image.departmentId.toString() === selectedDepartment)
  ) || [];

  // Get department name - use departmentName from API if available, otherwise look it up
  const getDepartmentName = (image: any) => {
    if (image.departmentName) {
      return image.departmentName;
    }
    if (image.departmentId && departmentsData) {
      const dept = Array.isArray(departmentsData) 
        ? departmentsData.find((d: any) => d.id === image.departmentId)
        : departmentsData.find((d: any) => d.id === image.departmentId);
      return dept?.name || 'Unknown Department';
    }
    return 'Unknown Department';
  };

  const handleEditTeamImage = (imageId: string) => {
    setLocation(`/edit-team-image/${imageId}`);
  };

  const handleDeleteTeamImage = () => {
    if (imageToDelete) {
      deleteTeamImageMutation.mutate(imageToDelete.id);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team images...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <Camera className="h-8 w-8 text-servify-purple" />
              <h1 className="text-4xl font-bold text-servify-dark-grey">Team Media</h1>
            </div>
          </div>
          
          {/* Add Team Image Button */}
          <Button
            onClick={() => setLocation('/add-team-image')}
            className="bg-servify-purple hover:bg-servify-purple/90 text-white shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid="button-add-team-image"
          >
            <Plus className="mr-2 h-5 w-5" />
            Add Team Media
          </Button>
        </div>

        {/* Controls Section */}
        <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-4 relative z-10">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Filter by Department:</span>
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select Department" />
              </SelectTrigger>
              <SelectContent className="z-[9999] relative bg-white border border-gray-200 shadow-lg" position="popper" sideOffset={4}>
                <SelectItem value="all">All Departments</SelectItem>
                {departmentsData && Array.isArray(departmentsData) && departmentsData.map((department: any) => (
                  <SelectItem key={department.id} value={department.id.toString()}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
        </div>

        {/* Team Images Grid */}
        {filteredTeamImages.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeamImages.map((teamImage) => (
              <Card 
                key={teamImage.id} 
                className="group hover:shadow-xl hover:shadow-servify-purple/20 transition-all duration-300 border-2 border-transparent hover:border-servify-purple/20 overflow-hidden"
                data-testid={`team-image-card-${teamImage.id}`}
              >
                {/* Media Section */}
                <div className="relative overflow-hidden">
                  {(() => {
                    const mediaInfo = getMediaTypeInfo(teamImage.fileName);
                    const MediaIcon = mediaInfo.icon;
                    
                    if (mediaInfo.type === 'video') {
                      return (
                        <div className="relative w-full h-48 bg-gray-900 flex items-center justify-center group/video">
                          <video
                            src={teamImage.mediaUrl}
                            className="w-full h-full object-cover"
                            controls={false}
                            preload="metadata"
                            onError={(e) => {
                              const video = e.target as HTMLVideoElement;
                              const parent = video.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                  <div class="w-full h-48 bg-gray-100 flex flex-col items-center justify-center">
                                    <svg class="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                    </svg>
                                    <span class="text-gray-500 text-sm">Video not available</span>
                                  </div>
                                `;
                              }
                            }}
                            data-testid={`team-video-${teamImage.id}`}
                          />
                          {/* Video Play Overlay */}
                          <div 
                            className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover/video:opacity-100 transition-opacity duration-300 cursor-pointer"
                            onClick={() => setSelectedVideo(teamImage)}
                          >
                            <div className="bg-white bg-opacity-90 rounded-full p-3 transform group-hover/video:scale-110 transition-transform duration-300">
                              <Play className="h-8 w-8 text-gray-800 ml-1" />
                            </div>
                          </div>
                          {/* Media Type Badge */}
                          <div className="absolute top-3 left-3">
                            <Badge className={`${mediaInfo.color} flex items-center space-x-1 text-xs font-medium`}>
                              <Video className="h-3 w-3" />
                              <span>{mediaInfo.badge}</span>
                            </Badge>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="relative">
                          <img
                            src={teamImage.mediaUrl}
                            alt={teamImage.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                            }}
                            data-testid={`team-image-${teamImage.id}`}
                          />
                          {/* Media Type Badge */}
                          <div className="absolute top-3 left-3">
                            <Badge className={`${mediaInfo.color} flex items-center space-x-1 text-xs font-medium`}>
                              <MediaIcon className="h-3 w-3" />
                              <span>{mediaInfo.badge}</span>
                            </Badge>
                          </div>
                        </div>
                      );
                    }
                  })()}
                </div>

                {/* Content Section */}
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-bold text-servify-dark-grey line-clamp-2">
                      {teamImage.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {teamImage.description && (
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                      {teamImage.description}
                    </p>
                  )}
                  
                  {/* Department and Metadata */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Building2 className="h-4 w-4 text-servify-purple" />
                      <Badge variant="outline" className="border-servify-purple text-servify-purple">
                        {getDepartmentName(teamImage)}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{new Date(teamImage.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {(() => {
                          const mediaInfo = getMediaTypeInfo(teamImage.fileName);
                          const MediaIcon = mediaInfo.icon;
                          return (
                            <>
                              <MediaIcon className="h-3 w-3" />
                              <span>{mediaInfo.badge}</span>
                            </>
                          );
                        })()}
                      </div>
                      {teamImage.fileSize && (
                        <div className="flex items-center space-x-1 col-span-2">
                          <FileImage className="h-3 w-3" />
                          <span>
                            {teamImage.fileName} • {(teamImage.fileSize / 1024 / 1024).toFixed(1)} MB
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditTeamImage(teamImage.id)}
                          className="flex-1 border-servify-purple text-servify-purple hover:bg-servify-purple hover:text-white"
                          data-testid={`button-edit-team-image-${teamImage.id}`}
                        >
                          <Edit3 className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-10 h-8 p-0 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          data-testid={`button-delete-team-image-${teamImage.id}`}
                          onClick={() => setImageToDelete(teamImage)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="space-y-6">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                <Camera className="h-12 w-12 text-gray-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-semibold text-gray-700">No Team Media Found</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {selectedDepartment === "all" 
                    ? "No team media have been uploaded yet. Share photos and videos of your team by clicking 'Add Team Media' to get started."
                    : "No team media found for the selected department. Try selecting a different department or upload new images and videos."
                  }
                </p>
              </div>
              <Button
                onClick={() => setLocation('/add-team-image')}
                className="bg-servify-purple hover:bg-servify-purple/90 text-white"
                data-testid="button-add-first-team-image"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Team Media
              </Button>
            </div>
          </div>
        )}

      </div>

      {/* Enhanced Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Video className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900 truncate">{selectedVideo.title}</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedVideo(null)}
                className="h-8 w-8 p-0 hover:bg-gray-100"
                data-testid="button-close-video-modal"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-4 max-h-[calc(90vh-80px)] overflow-y-auto">
              <div className="space-y-4">
                {/* Video Container */}
                <div className="relative bg-black rounded-lg overflow-hidden">
                  <video
                    key={selectedVideo.id}
                    src={selectedVideo.mediaUrl}
                    controls
                    className="w-full h-auto max-h-[60vh] object-contain"
                    onError={(e) => {
                      console.error('Video failed to load:', selectedVideo.mediaUrl);
                      const video = e.target as HTMLVideoElement;
                      const container = video.parentElement;
                      if (container) {
                        container.innerHTML = `
                          <div class="w-full h-64 bg-gray-100 flex flex-col items-center justify-center rounded-lg">
                            <svg class="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <span class="text-gray-600 font-medium">Unable to load video</span>
                            <span class="text-gray-500 text-sm mt-1">The video file may be corrupted or unavailable</span>
                          </div>
                        `;
                      }
                    }}
                    data-testid={`modal-video-${selectedVideo.id}`}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>

                {/* Video Information */}
                <div className="space-y-4">
                  {selectedVideo.description && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{selectedVideo.description}</p>
                    </div>
                  )}
                  
                  {/* Metadata Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-4 w-4 text-servify-purple flex-shrink-0" />
                      <div>
                        <span className="text-xs text-gray-500 block">Department</span>
                        <span className="text-sm font-medium text-gray-900">{getDepartmentName(selectedVideo)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <div>
                        <span className="text-xs text-gray-500 block">Upload Date</span>
                        <span className="text-sm font-medium text-gray-900">{new Date(selectedVideo.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <FileVideo className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <div>
                        <span className="text-xs text-gray-500 block">File Name</span>
                        <span className="text-sm font-medium text-gray-900 truncate" title={selectedVideo.fileName}>
                          {selectedVideo.fileName}
                        </span>
                      </div>
                    </div>
                    
                    {selectedVideo.fileSize && (
                      <div className="flex items-center space-x-3">
                        <FileImage className="h-4 w-4 text-purple-600 flex-shrink-0" />
                        <div>
                          <span className="text-xs text-gray-500 block">File Size</span>
                          <span className="text-sm font-medium text-gray-900">
                            {(selectedVideo.fileSize / 1024 / 1024).toFixed(1)} MB
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!imageToDelete} onOpenChange={(open) => !open && setImageToDelete(null)}>
        <AlertDialogContent className="bg-white border-2 border-gray-300 shadow-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold text-gray-900">Delete Team Image</AlertDialogTitle>
            <AlertDialogDescription className="text-base text-gray-700 mt-3 leading-relaxed">
              Are you sure you want to delete <span className="font-semibold text-gray-900">"{imageToDelete?.title}"</span>? 
              <br />
              <br />
              This action cannot be undone and will permanently remove the team image.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel 
              onClick={() => setImageToDelete(null)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 border-2 border-gray-300 font-semibold px-6"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeamImage}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 shadow-lg"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}